'use client';

import { VpIcon } from '@vtmn-play/react';
import { useLocale } from 'next-intl';
import clsx from 'clsx';
import { useMemo } from 'react';
import { DateTime } from 'luxon';
import { formatAppointmentDate } from '@/helpers/day';
import {
  BookingServiceModalityEnum,
  BookingStatus,
} from '@repo/common/interfaces';
import { categoryIcons } from '@repo/common/config';

import './ReassuranceSection.scss';

interface ReassuranceProps {
  bookingStatus?: BookingStatus;
}

const ReassuranceSection = ({ bookingStatus }: ReassuranceProps) => {
  const locale = useLocale();

  const name = bookingStatus?.service?.productSheet?.name ?? undefined;
  const category = bookingStatus?.service?.productSheet?.category ?? undefined;
  const store = bookingStatus?.shop?.attributes?.name ?? undefined;
  const availabilityOptionStartDate = useMemo(() => {
    const startSec = bookingStatus?.timeSlot?.startSec;

    if (!startSec) {
      return undefined;
    }

    return DateTime.fromSeconds(startSec, {
      zone: 'utc',
    }).toJSDate();
  }, [bookingStatus?.timeSlot]);

  const formattedAppointmentDate = useMemo(() => {
    if (!availabilityOptionStartDate) {
      return '';
    }

    return formatAppointmentDate(
      availabilityOptionStartDate,
      locale,
      bookingStatus?.shop?.timeZone,
    );
  }, [availabilityOptionStartDate]);

  const timeslot = formattedAppointmentDate ?? undefined;

  const showStoreName = useMemo(() => {
    return (
      bookingStatus?.service?.modality !== BookingServiceModalityEnum.REMOTE
    );
  }, [category, bookingStatus]);

  return (
    <div
      className={clsx(
        'reassurance px-l py-m',
        !name && !store && !timeslot ? 'hidden' : 'flex',
      )}
    >
      {name && (
        <div id='reassurance-service-step' className='stepper-step'>
          <VpIcon
            name={
              category
                ? categoryIcons?.[category]
                : 'circularity-maintain-repair'
            }
            size={20}
          />
          <p>{name}</p>
        </div>
      )}

      {showStoreName && store && (
        <div id='reassurance-store-step' className='stepper-step'>
          <VpIcon name='map-pin' size={20} />
          <p>{store}</p>
        </div>
      )}

      {timeslot && (
        <div id='reassurance-time-slot-step' className='stepper-step'>
          <VpIcon name='time' size={20} />
          <p>{timeslot}</p>
        </div>
      )}
    </div>
  );
};

export default ReassuranceSection;
